import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import TextField from "../../components/primitives/TextField";
import PasswordField from "../../components/primitives/PasswordField";
import TextButton from "../../components/primitives/TextButton";
import SegmentedControl from "../../components/primitives/SegmentedControl";
import FormError from "../../components/primitives/FormError";
import { API_BASE_URL } from "../../config";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

function GoogleIcon({ size = 16 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            aria-hidden="true"
        >
            <path
                fill="#EA4335"
                d="M12 10.2v3.96h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.32 0-6.02-2.74-6.02-6.12s2.7-6.12 6.02-6.12c1.88 0 3.14.8 3.86 1.48l2.64-2.54C16.78 3.32 14.6 2.4 12 2.4 6.78 2.4 2.5 6.66 2.5 11.94S6.78 21.48 12 21.48c6.94 0 9.5-4.86 9.5-7.36 0-.5-.06-.88-.14-1.26H12z"
            />
        </svg>
    );
}

const looksLikeEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s ?? "").trim());

function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, login, requestMagicLink, resendVerification } = useAuth();

    const [mode, setMode] = useState("password");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [serverErr, setServerErr] = useState(null);
    const [errCode, setErrCode] = useState(null);
    const [resendBusy, setResendBusy] = useState(false);
    const [resendSent, setResendSent] = useState(false);
    const emailRef = useRef(null);

    useEffect(() => {
        if (user) {
            const next = location.state?.next || "/dashboard";
            navigate(next, { replace: true });
        }
    }, [user, navigate, location.state]);

    useEffect(() => {
        emailRef.current?.focus?.();
    }, [mode]);

    const handleGoogle = () => {
        const next = location.state?.next || "/dashboard";
        window.location.href = `${API_BASE_URL}/login/google?next=${encodeURIComponent(next)}`;
    };

    const submitPassword = async (e) => {
        e?.preventDefault?.();
        setServerErr(null);
        setErrCode(null);
        if (!looksLikeEmail(email)) {
            setServerErr("Enter a valid email address.");
            return;
        }
        if (!password || password.length < 8) {
            setServerErr("Password must be at least 8 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await login({ email: email.trim(), password });
            navigate("/dashboard", { replace: true });
        } catch (err) {
            const code = err instanceof ApiError ? err.code : null;
            setErrCode(code);
            if (code === "invalid_credentials") {
                setServerErr("Invalid email or password.");
            } else if (code === "email_not_verified") {
                setServerErr(
                    "This account isn't verified yet. Check your inbox for the verification link."
                );
            } else if (code === "too_many_requests") {
                setServerErr("Too many attempts. Try again in a minute.");
            } else {
                setServerErr(
                    err instanceof ApiError
                        ? err.message
                        : err?.message || "Sign-in failed."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submitMagic = async (e) => {
        e?.preventDefault?.();
        setServerErr(null);
        setErrCode(null);
        if (!looksLikeEmail(email)) {
            setServerErr("Enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await requestMagicLink(email.trim());
            navigate("/auth/check-email", {
                state: { email: email.trim(), intent: "magic" },
            });
        } catch (err) {
            if (err instanceof ApiError && err.code === "too_many_requests") {
                setServerErr("Too many attempts. Try again in a minute.");
            } else {
                setServerErr(
                    err instanceof ApiError
                        ? err.message
                        : err?.message || "Could not send link."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendVerify = async () => {
        if (!email) return;
        setResendBusy(true);
        try {
            await resendVerification(email.trim());
            setResendSent(true);
        } catch {
            // Ignore — the BE always returns 200 on this endpoint anyway.
            setResendSent(true);
        } finally {
            setResendBusy(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Console / Sign-in"
            title="Welcome back."
            lede="Sign in to access your console, manifests, and SKU catalog."
            footer={
                <>
                    <span>New here?</span>
                    <Link to="/sign-up" className="auth-shell-link auth-shell-link--accent">
                        Create an account →
                    </Link>
                </>
            }
        >
            <button
                type="button"
                onClick={handleGoogle}
                className="auth-btn auth-btn--google focus-ring"
            >
                <GoogleIcon size={16} />
                Sign in with Google
            </button>

            <div className="auth-divider" aria-hidden="true">
                <span>or</span>
            </div>

            <SegmentedControl
                ariaLabel="Sign-in method"
                value={mode}
                onChange={setMode}
                options={[
                    { value: "password", label: "Password" },
                    { value: "magic", label: "Magic link" },
                ]}
            />

            {mode === "password" ? (
                <form className="auth-form" onSubmit={submitPassword} noValidate>
                    <TextField
                        ref={emailRef}
                        type="email"
                        label="Email"
                        required
                        value={email}
                        onChange={setEmail}
                        placeholder="you@company.com"
                        autoComplete="email"
                    />
                    <PasswordField
                        label="Password"
                        required
                        value={password}
                        onChange={setPassword}
                        placeholder="At least 8 characters"
                        autoComplete="current-password"
                    />

                    <div className="auth-form__inline">
                        <Link to="/forgot-password" className="auth-shell-link">
                            Forgot password?
                        </Link>
                    </div>

                    {serverErr && <FormError>{serverErr}</FormError>}

                    {errCode === "email_not_verified" && (
                        <button
                            type="button"
                            className="auth-resend focus-ring"
                            onClick={handleResendVerify}
                            disabled={resendBusy || resendSent}
                        >
                            <Mail size={12} aria-hidden="true" />
                            {resendSent
                                ? "Verification email sent"
                                : resendBusy
                                    ? "Sending…"
                                    : "Resend verification email"}
                        </button>
                    )}

                    <TextButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={submitting}
                        iconRight={ArrowRight}
                    >
                        Sign in
                    </TextButton>
                </form>
            ) : (
                <form className="auth-form" onSubmit={submitMagic} noValidate>
                    <TextField
                        ref={emailRef}
                        type="email"
                        label="Email"
                        required
                        value={email}
                        onChange={setEmail}
                        placeholder="you@company.com"
                        autoComplete="email"
                        hint="We'll send a one-time sign-in link to your inbox."
                    />

                    {serverErr && <FormError>{serverErr}</FormError>}

                    <TextButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={submitting}
                        iconRight={ArrowRight}
                    >
                        Send sign-in link
                    </TextButton>
                </form>
            )}
        </AuthShell>
    );
}

export default LoginPage;
